import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate, Navigate, Outlet } from 'react-router-dom';
import { useAuth, hasRole, canSeeCallMonitoring, canMakeTemplates, canSeeSuperBoard, canOpenTemplates, canOpenAgents, puedeModoInspector, puedeHacerPagos, puedeImprimirCheques } from '../lib/auth';
import { useMyDefaultDepartment } from '../lib/useMyDepartment';
import { ForcePasswordChange } from './ForcePasswordChange';
import { useI18n } from '../lib/i18n';
import { api } from '../lib/api';
import logoWhite from '../assets/proactive-logo-white.png';
import {
  LayoutDashboard, Search, History, FolderKanban, Banknote, TrendingDown,
  Hammer, FilePen, BadgeCheck, Hourglass, CircleDollarSign, Home, HardHat, Wallet, LifeBuoy,
  RotateCcw, PenLine, Landmark, AlertTriangle, FileCheck, ShieldCheck, Compass, Inbox,
  Printer,
  Building2, Wrench, Users, BarChart3, Settings as SettingsIcon, CalendarDays,
  ChevronRight, ChevronDown, PanelLeftClose, PanelLeftOpen, Menu, ArrowLeft, Upload, Mail, Bell,
  CalendarRange, Activity, X, Headphones, NotebookPen, Camera, MessageSquare, User,
  CircleHelp, Archive, Scale, Hand, Plus, FileText, FilePenLine, Trophy, Clock4,
  Megaphone, TrendingUp, CalendarMinus, FilePlus, ClipboardList, ThumbsUp, Gauge, Video, Radar, Tv, GraduationCap, Sparkles, type LucideIcon,
  UserRound, Smartphone, Tags, Ruler,
  Calculator, LayoutTemplate, Bot,
} from 'lucide-react';
import { EomAnnouncementModal } from './EomAnnouncementModal';
import { Avatar } from './Avatar';
import { PresenceProvider } from '../lib/presence';
import { ProfileMenu } from './ProfileMenu';
import { CommandSearch } from './CommandSearch';
import { ClockButton } from './ClockButton';
import { CocAlertBanner } from './CocAlertBanner';
import { SocialAlertBanner } from './SocialAlertBanner';
import { SmsAlert } from './SmsAlert';
import { EsxRequiredHost } from './EsxRequiredHost';
import { UndoSendHost } from './UndoSendHost';
import { vaciarCola, hayEnviosEsperando } from '../lib/envioConDeshacer';
import { ScrollTopButton } from './ScrollTopButton';
import { AppUpdateBar } from './AppUpdateBar';
import { BroadcastBanner } from './BroadcastBanner';
import { PayslipBanner } from './PayslipBanner';
import { InspectorAlertBanner } from './InspectorAlertBanner';
import { NewVideoBanner } from './rec/NewVideoBanner';
import { FalloDeCargaBar } from './FalloDeCargaBar';
import { EnablePushButton } from './EnablePushButton';
import { KpiDelDia } from './KpiDelDia';
import { ShareButton } from './share/ShareButton';
import { InspectorModeButton } from './InspectorModeButton';

const ICONS: Record<string, LucideIcon> = {
  '/': LayoutDashboard,
  '/ai': Sparkles,
  '/super-board': Radar,
  '/office-pulse': Tv,
  '/templates': LayoutTemplate,
  '/agents': Bot,
  '/marketing': Megaphone,
  '/diary': NotebookPen,
  '/finder': Search,
  '/last-viewed': History,
  '/claims': FolderKanban,
  '/messaging': Mail,
  '/notifications': Bell,
  '/queue/acv': Banknote,
  '/queue/depreciation': TrendingDown,
  '/queue/approved-repair': Hammer,
  '/queue/invoice-todo': FilePen,
  '/queue/review': BadgeCheck,
  '/waiting-to-clear': Hourglass,
  '/ready-to-pay': CircleDollarSign,
  '/homeowner-with-check': Home,
  '/contractor-with-check': HardHat,
  '/pay-queue': Wallet,
  '/owed-to-us': RotateCcw,
  '/request-payment-auth': PenLine,
  '/queue/mortgage': Landmark,
  '/bounced-checks': AlertTriangle,
  '/checks': FileCheck,
  '/checks-issued': Printer,
  '/coverage-detector': ShieldCheck,
  '/inspectors': Compass,
  '/dept/scheduling/dashboard': LayoutDashboard,
  '/dept/supplements/dashboard': BarChart3,
  '/dept/supplements': FolderKanban,
  '/dept/supplements/estimating': Calculator,
  '/dept/supplements/price-lists': Tags,
  '/dept/supplements/verified-measurements': Ruler,
  '/dept/scheduling/scheduled': CalendarDays,
  '/dept/scheduling/calendar': CalendarRange,
  '/dept/scheduling/uploads': Upload,
  '/dept/scheduling/reports': BarChart3,
  '/dept/scheduling/inspector-history': History,
  '/dept/scheduling/activity': Activity,
  '/intake-review': Inbox,
  '/intake-finder': Search,
  '/insurance': Landmark,
  '/contractors': Wrench,
  '/jobflow': Building2,
  '/teams': Users,
  '/reports': BarChart3,
  '/requests': Hand,
  '/call-monitoring': Headphones,
  '/recordings': Video,
  '/training': GraduationCap,
  '/email-health': Mail,
  '/settings': SettingsIcon,
  // Rediseño TP Fase 2 — mapa canónico (antes estos ítems caían al chevron genérico):
  '/company': BarChart3,
  '/contractor-inbox': HardHat,
  '/concerns': LifeBuoy,
  '/contractor-photos': Camera,
  '/client-portal': UserRound,
  '/claimroute': Smartphone,
  '/claimcam': Camera,
  '/queue/supplement': Plus,
  '/archive': Archive,
  '/complaints': Scale,
  '/kudos': ThumbsUp,
  '/sms': MessageSquare,
  '/dept/mine/inbox': User,
  '/dept/unassigned/inbox': CircleHelp,
  '/contracts': FileText,
  '/intake-form': FilePenLine,
  '/evaluation': Trophy,
  '/timesheet': Clock4,
  '/payroll': Banknote,
  '/broadcast': Megaphone,
  '/risk': Activity,
  '/company-board': Gauge,
  '/system-health': TrendingUp,
  '/dept/scheduling/time-off': CalendarMinus,
  '/new-claims': FilePlus,
  '/dept/scheduling': LayoutDashboard,
  '/dept/intake': LayoutDashboard,
  '/dept/claims': LayoutDashboard,
};
function iconFor(to: string): LucideIcon {
  if (ICONS[to]) return ICONS[to];
  // Buzones dinámicos (por PA, etc.): siempre ícono de inbox, nunca el chevron genérico.
  if (to.endsWith('/inbox')) return Inbox;
  if (to.startsWith('/dept/') && to.includes('/queue/')) return ClipboardList;
  return ChevronRight;
}

type NavDef = {
  to: string;
  key: string;
  min?: 'manager' | 'super-manager';
  /** Permiso por-persona (no por rol). Hoy solo Call Monitoring: lo decide el dueño a dedo. */
  perm?: 'callMonitoring' | 'templates' | 'templatesHub' | 'agentsHub' | 'superBoard' | 'canPay' | 'printChecks';
  /**
   * Interruptor de INFRA (una variable de Railway), no un permiso de persona. Apagado, el ítem no
   * existe: su backend responde 404 y un link que lleva a una pantalla rota es peor que no tenerlo.
   */
  flag?: 'portal';
  badge?: 'payQueue' | 'requestAuth' | 'review' | 'invoiceTodo' | 'messaging' | 'notifications' | 'complaints' | 'sms' | 'requests' | 'contractor' | 'contractorPhotos' | 'callMonitoring' | 'kudos' | 'recordings' | 'concerns';
  mailKey?: string; // buzón de email: muestra el badge de "por atender" (counts['mail_<key>'])
};

// BARRA SUPERIOR (pedido de Manuel): accesos rápidos globales. Finder va como BUSCADOR
// (aparte); estos 7 son links con su badge. Antes vivían en el menú izquierdo.
const TOPBAR_NAV: NavDef[] = [
  { to: '/claims', key: 'nav.claims' },
  { to: '/last-viewed', key: 'nav.lastViewed' },
  { to: '/messaging', key: 'nav.messaging',  badge: 'messaging' },
  { to: '/notifications', key: 'nav.notifications',  badge: 'notifications' },
  { to: '/archive', key: 'nav.archive' },
  // KUDOS arriba (manager+, 2026-08-11, pedido del dueño): "los managers que ven los kudos y
  // deciden employee [of the month] que puedan ver los kudos y review them". La cola de revisión
  // estaba enterrada DENTRO de la pestaña Employee of the Month; el que la tiene que trabajar todos
  // los días no la encontraba. El badge cuenta los kudos pendientes de revisar.
  { to: '/kudos', key: 'nav.kudos',  min: 'manager', badge: 'kudos' },
  // Complaints SALIÓ de esta barra (mismo pedido) — pero NO se perdió: es la página personal del
  // empleado (sus quejas + su voto del mes) y bajó al menú izquierdo, con su badge. Sacarla de
  // acá sin darle otra casa la habría dejado sin ninguna puerta.
  { to: '/requests', key: 'nav.requests',  min: 'super-manager', badge: 'requests' },
];

// Bandejas de entrada del menú izquierdo (van PRIMERO): el contractor inbox + los buzones.
const INBOX_TOP_NAV: NavDef[] = [
  // PROACTIVE AI (pedido del dueño 2026-10-02, mig 0473): los asistentes por OpenRouter que reemplazan
  // la cuenta compartida de ChatGPT. Lo ve toda la oficina; inspector y TV no llegan a este menú.
  { to: '/ai', key: 'nav.ai' },
  // CLAIMCAM (dueño 2026-10-10): la app de fotos propia que reemplaza a CompanyCam. Proyecto = claim.
  { to: '/claimcam', key: 'nav.claimcam' },
  { to: '/diary', key: 'nav.diary' },
  // Mis quejas + mi voto del mes. Vivía en la barra de arriba hasta el 2026-08-11; es una página
  // PERSONAL (no la de los managers, que es la pestaña de /evaluation), y acá está junto al To Do,
  // que es el otro lugar donde uno mira lo suyo.
  { to: '/complaints', key: 'nav.complaints', badge: 'complaints' },
  // Cómo va la compañía (manager+): contadores, contratos por contratista y las colas con sus días.
  { to: '/company', key: 'nav.company',  min: 'manager' },
  { to: '/contractor-inbox', key: 'nav.contractorInbox',  badge: 'contractor' },
  // Casos de clientes: lo que entra por resolve.html del website (fase 2). Badge = tickets nuevos.
  { to: '/concerns', key: 'nav.concerns', badge: 'concerns' },
  // Sección PROPIA, debajo del Inbox: las fotos que mandan los contratistas (antes vivía
  // adentro del Inbox como una card y mostraba siempre cero).
  // Badge = fotos de contratista NUEVAS (últimos 7 días) — pedido del dueño 2026-08-09.
  { to: '/contractor-photos', key: 'nav.contractorPhotos', badge: 'contractorPhotos' },
  // CLIENT PORTAL (pedido del dueño 2026-09-28): se escribe el nombre del homeowner y se ve
  // exactamente lo que ve él, con el panel de acceso al lado. office+ porque es la pantalla de
  // soporte: el cliente llama y quien atiende tiene que ver lo mismo que él.
  { to: '/client-portal', key: 'nav.clientPortal', flag: 'portal' },
  // CLAIMROUTE (pedido del dueño 2026-10-05): la hermana del Client Portal para el campo. Se elige
  // el inspector y se ve SU app tal como él la ve en el teléfono, solo lectura, para guiarlo.
  { to: '/claimroute', key: 'nav.claimRoute' },
  // Gestión del acceso de contratistas a JobFlow (invitar/reenviar/reset/view-as) — todos.
  { to: '/jobflow', key: 'nav.jobflow' },
  // Screen recordings (pedido del dueño 2026-09-10): mis grabaciones + las que me mandaron.
  // El badge cuenta los videos recibidos SIN ver (lo publica el banner "nuevo video").
  { to: '/recordings', key: 'nav.recordings', badge: 'recordings' },
  // TRAINING (pedido del dueño 2026-10-02): videos y lecturas por departamento + la iniciación de
  // quien entra. Lo ve toda la oficina; administrar (y el inbox de lo mandado desde Record) es super-manager+.
  { to: '/training', key: 'nav.training' },
];

/* SOLO TELÉFONO (≤900px). La barra superior con los accesos rápidos NO existe en el teléfono
   (el CSS esconde .topbar-nav para dejar el appbar limpio), así que estos ítems se quedaban sin
   ninguna puerta: el Archivo no se podía abrir desde el celular. Van dentro de .nav-mobile-only,
   que el CSS muestra únicamente en el menú del teléfono — en desktop siguen viviendo arriba y
   repetirlos acá sería ruido. */
const MOBILE_ONLY_NAV: NavDef[] = [
  { to: '/archive', key: 'nav.archive' },
];

// Menú específico de Payments (sus colas), debajo de la base común.
const PAYMENTS_NAV: NavDef[] = [
  { to: '/queue/acv', key: 'nav.acv' },
  { to: '/queue/depreciation', key: 'nav.depreciation' },
  { to: '/queue/approved-repair', key: 'nav.approvedRepair' },
  { to: '/queue/supplement', key: 'nav.supplementCheck' },
  { to: '/queue/invoice-todo', key: 'nav.invoiceTodo',  badge: 'invoiceTodo' },
  { to: '/queue/review', key: 'nav.review',  min: 'manager', badge: 'review' },
  { to: '/waiting-to-clear', key: 'nav.waitingClear',  min: 'manager' },
  { to: '/ready-to-pay', key: 'nav.readyToPay',  min: 'manager' },
  { to: '/homeowner-with-check', key: 'nav.homeownerCheck' },
  { to: '/contractor-with-check', key: 'nav.contractorCheck' },
  { to: '/pay-queue', key: 'nav.payQueue',  badge: 'payQueue' },
  { to: '/owed-to-us', key: 'nav.owedToUs',  min: 'manager' },
  { to: '/request-payment-auth', key: 'nav.requestAuth',  badge: 'requestAuth' },
  { to: '/queue/mortgage', key: 'nav.mortgage' },
  { to: '/bounced-checks', key: 'nav.bounced',  min: 'manager' },
  { to: '/checks', key: 'nav.checks' },
  // El cheque que SALE. Lo ve quien puede pagar Y el super-manager del depto de Payments (dueño
  // 2026-10-27: "que super manager de payments pueda verlo y desde ahí imprimirlos todos, pero no
  // pueda generar cheques"). Es `perm` y no `min`: no se abre por rol a secas.
  { to: '/checks-issued', key: 'nav.checksIssued', perm: 'printChecks' },
];

// Buzones de email por departamento (plataforma tipo Missive).
const MAIL_NAV: NavDef[] = [
  { to: '/sms', key: 'nav.smsInbox',  badge: 'sms' },
  { to: '/dept/mine/inbox', key: 'nav.myMail',  mailKey: 'mine' },
  { to: '/dept/intake/inbox', key: 'nav.intakeMail',  mailKey: 'intake' },
  { to: '/dept/claims/inbox', key: 'nav.claimMail',  mailKey: 'claims' },
  { to: '/dept/supplements/inbox', key: 'nav.supplementsMail',  mailKey: 'supplements' },
  { to: '/dept/payments/inbox', key: 'nav.paymentsMail',  mailKey: 'payments' },
  { to: '/dept/unassigned/inbox', key: 'nav.unassignedMail',  mailKey: 'unassigned' },
];

// Administración (global), siempre al final.
const MANAGEMENT_NAV: NavDef[] = [
  // Super Board (SUPER_BOARD_PLAN.md): el tablero único de la office manager. El gate es la LISTA de
  // Settings → Team (perm), no el rol: quien no está en la lista ni lo ve ni lo abre.
  { to: '/super-board', key: 'nav.superBoard', perm: 'superBoard' },
  // Office pulse (2026-09-16): la pared de los televisores, dentro del app y para TODOS (sin dinero); desde
  // acá se toca una gráfica, se ve la lista y se abre el claim.
  { to: '/office-pulse', key: 'nav.officePulse' },
  // TEMPLATES (dueño 2026-10-28, mig 0553): todas las plantillas de email y SMS en un lugar, por departamento.
  // El gate es la LISTA de su pestaña Access (Paula, Jessika y el master), no el rol.
  { to: '/templates', key: 'nav.templates', perm: 'templatesHub' },
  // AGENTS (dueño 2026-11-01, mig 0562): el portal de los bots (dashboard, corridas, reglas). Gate = la LISTA de su pestaña Access.
  { to: '/agents', key: 'nav.agents', perm: 'agentsHub' },
  // MARKETING (dueño 2026-11-02, mig 0563): prospectos, campañas aprobadas y respuestas. Mismo gate que Agents.
  { to: '/marketing', key: 'nav.marketing', perm: 'agentsHub' },
  // Dashboard de management: landing de los usuarios sin departamento (= managers).
  { to: '/management', key: 'nav.mgmtDashboard',  min: 'manager' },
  { to: '/coverage-detector', key: 'nav.coverageDetector',  min: 'manager' },
  { to: '/insurance', key: 'nav.insurance' },
  // HO Updates: la cola de los updates semanales al homeowner. Cada quien revisa lo suyo.
  { to: '/ho-updates', key: 'nav.hoUpdates' },
  { to: '/contractors', key: 'nav.contractors',  min: 'super-manager' },
  { to: '/contracts', key: 'nav.contracts',  min: 'super-manager' },
  { to: '/intake-form', key: 'nav.intakeForm',  min: 'super-manager' },
  { to: '/teams', key: 'nav.teams',  min: 'super-manager' },
  { to: '/evaluation', key: 'nav.evaluation',  min: 'manager' },
  // Hoja de horas del equipo. El empleado ve LAS SUYAS desde el reloj de la barra (/my-hours).
  { to: '/timesheet', key: 'nav.timesheet',  min: 'manager' },
  // Nómina: super-manager, porque acá se ve cuánto gana cada persona.
  { to: '/payroll', key: 'nav.payroll',  min: 'super-manager' },
  // Broadcast: el gate real es la lista que arma el master, no el rol. Se muestra a manager+
  // y el backend rebota a quien no esté habilitado.
  { to: '/broadcast', key: 'nav.broadcast',  min: 'manager' },
  // Semáforo: la única vista que muestra lo que NO se está haciendo. El resto de las colas
  // muestran lo que hay que hacer; un claim abandonado no aparece en ninguna de ellas.
  { to: '/risk', key: 'nav.risk',  min: 'manager' },
  // El tablero de medición: embudo, ciclo, dinero y las alertas de "para atender hoy". Tenía
  // ruta pero NINGÚN enlace — se llegaba escribiendo la URL, que es lo mismo que no existir.
  { to: '/company-board', key: 'nav.companyBoard',  min: 'super-manager' },
  { to: '/reports', key: 'nav.reports',  min: 'manager' },
  // Efectividad: los números que se le muestran a un CLIENTE. Manager+ los mira; crear un link
  // público es de super-manager y lo valida el backend.
  { to: '/effectiveness', key: 'nav.effectiveness',  min: 'manager' },
  // Depreciación: la cola de COC sin cheque. Es plata emitida que no llegó y no aparecía en
  // ninguna otra pantalla — las colas muestran lo que hay que hacer, no lo que se perdió.
  { to: '/depreciation', key: 'nav.depreciationOwed',  min: 'manager' },
  // Alertas de IA sobre las llamadas grabadas (cliente molesto, empleado grosero, amenazas,
  // temas legales…) — solo super-manager, pedido 2026-07-30.
  { to: '/call-monitoring', key: 'nav.callMonitoring',  perm: 'callMonitoring', badge: 'callMonitoring' },
  { to: '/email-health', key: 'nav.emailHealth',  min: 'super-manager' },
  { to: '/system-health', key: 'nav.systemHealth',  min: 'super-manager' },
  // MANAGER ve Settings solo con la pestaña Templates (la página recorta sola, 2026-08-11).
  // Settings: manager+ por rol, o quien tenga el permiso de plantillas aunque no sea manager
  // (2026-08-11). La página recorta sola y deja SOLO la pestaña Templates.
  { to: '/settings', key: 'nav.settings',  min: 'manager', perm: 'templates' },
];

// Herramientas (código) del menú izquierdo por departamento, además de sus colas/estatus.
const DEPT_TOOLS: Record<string, NavDef[]> = {
  claims: [{ to: '/coverage-detector', key: 'nav.coverageDetector' }],
  // Scheduling: las vistas (Welcome Call, Ready to be filed…) se rinden ENTRE el
  // Dashboard (home) y estas herramientas. Ver renderNav abajo.
  scheduling: [
    { to: '/dept/scheduling/scheduled', key: 'nav.crScheduled' },
    { to: '/dept/scheduling/calendar', key: 'nav.crCalendar' },
    { to: '/dept/scheduling/uploads', key: 'nav.crUploads' },
    { to: '/dept/scheduling/reports', key: 'nav.crReports' },
    { to: '/dept/scheduling/time-off', key: 'nav.timeOff',  min: 'manager' },
    { to: '/dept/scheduling/inspector-history', key: 'nav.inspectorHistory',  min: 'manager' },
    { to: '/dept/scheduling/activity', key: 'nav.crActivity',  min: 'manager' },
    { to: '/inspectors', key: 'nav.inspectors',  min: 'super-manager' },
  ],
  intake: [
    { to: '/new-claims', key: 'nav.newClaims' },
    { to: '/intake-review', key: 'nav.intakeReview',  min: 'manager' },
    { to: '/intake-finder', key: 'nav.intakeFinder' },
  ],
  supplements: [
    { to: '/dept/supplements/dashboard', key: 'nav.supDashboard' },
    { to: '/dept/supplements', key: 'nav.supBoard' },
    // La Estimating Tool como working view (dueño 2026-10-19): buscar cualquier cliente y estimar; por hacer / en curso / hechos.
    { to: '/dept/supplements/estimating', key: 'nav.supEstimating' },
    // Price lists de Xactimate por estado (dueño 2026-10-08): el archivo oficial de cada mes + la memoria aprendida.
    { to: '/dept/supplements/price-lists', key: 'nav.supPriceLists' },
    // Las medidas verificadas (1ESX) pedidas desde el botón: cola de aprobación, en proceso, entregadas (dueño 2026-10-13).
    { to: '/dept/supplements/verified-measurements', key: 'nav.supVerifiedMeasurements' },
  ],
};

// Rutas de herramientas que NO viven bajo /dept/:key pero pertenecen a un depto.
// Sin esto, al abrirlas el menú se caía a Payments.
const TOOL_DEPT: Record<string, string> = {
  '/inspectors': 'scheduling',
  '/coverage-detector': 'claims',
  '/intake-review': 'intake',
  '/intake-finder': 'intake',
  '/new-claims': 'intake',
};

/** Ítems del menú que el HUB del tab Inbox ya cubre — en móvil no se repiten en el Menú. */
const HUB_DUP = new Set(['/contractor-inbox', '/contractor-photos']);

/** Departamentos REALES. 'mine' y 'unassigned' son pseudo-buzones, no departamentos. */
const REAL_DEPTS = new Set(['payments', 'claims', 'supplements', 'scheduling', 'intake']);

/**
 * Depto que codifica una ruta (/dept/:key o herramienta de depto), o null si es común.
 *
 * OJO — los BUZONES DE CORREO no cuentan. Antes `/dept/claims/inbox` ponía el departamento de
 * trabajo en "claims" para siempre, y `/dept/mine/inbox` lo dejaba en "mine", que ni siquiera es
 * un departamento. Como ese valor decide en qué pestaña abre cada claim, alguien de Payments que
 * miraba una vez "Claim mail" pasaba a abrir TODOS los claims en Claims (o en Overview) sin
 * entender por qué (reporte de Ornella, 2026-07-31).
 */
function deptForPath(p: string): string | null {
  if (p.endsWith('/inbox')) return null;
  if (p.startsWith('/dept/')) {
    const key = decodeURIComponent(p.split('/')[2] || '');
    return REAL_DEPTS.has(key) ? key : null;
  }
  return TOOL_DEPT[p] ?? null;
}

/** Lo guardado puede estar sucio de la versión anterior ('mine', 'unassigned'): se descarta. */
function storedDept(): string | null {
  const v = localStorage.getItem('activeDept');
  return v && REAL_DEPTS.has(v) ? v : null;
}

const TITLE_KEYS: Record<string, string> = {
  '/': 'nav.dashboard',
  // Sin esta entrada el appbar caía a activeDeptName (el último depto clickeado en el menú) y el
  // dashboard de management salía titulado "Supplements" en el móvil (reporte 2026-08-09).
  '/management': 'nav.mgmtDashboard',
  '/super-board': 'nav.superBoard',
  '/office-pulse': 'nav.officePulse',
  '/templates': 'nav.templates',
  '/agents': 'nav.agents',
  '/marketing': 'nav.marketing',
  '/finder': 'nav.finder',
  '/diary': 'nav.diary',
  '/jobflow': 'nav.jobflow',
  '/claimroute': 'nav.claimRoute',
  '/claimcam': 'nav.claimcam',
  '/recordings': 'nav.recordings',
  '/training': 'nav.training',
  '/ai': 'nav.ai',
  '/company': 'nav.company',
  '/last-viewed': 'nav.lastViewed',
  '/invoice-todo': 'sec.invoiceTodoTitle',
  '/review': 'nav.review',
  '/checks': 'nav.checks',
  '/checks-issued': 'nav.checksIssued',
  '/waiting-to-clear': 'nav.waitingClear',
  '/ready-to-pay': 'nav.readyToPay',
  '/homeowner-with-check': 'nav.homeownerCheck',
  '/contractor-with-check': 'nav.contractorCheck',
  '/bounced-checks': 'nav.bounced',
  '/pay-queue': 'nav.payQueue',
  '/request-payment-auth': 'nav.requestAuth',
  '/owed-to-us': 'nav.owedToUs',
  '/mortgage': 'nav.mortgage',
  '/claims': 'nav.claims',
  // Sin esta entrada el appbar del teléfono caía a activeDeptName y /archive salía titulado
  // "Payments" (el último depto abierto en el menú), mismo bug que /management arriba.
  '/archive': 'nav.archive',
  '/new-claims': 'nav.newClaims',
  '/system-health': 'nav.systemHealth',
  '/messaging': 'nav.messaging',
  '/notifications': 'nav.notifications',
  '/insurance': 'nav.insurance',
  '/contractors': 'nav.contractors',
  '/teams': 'nav.teams',
  '/inspectors': 'nav.inspectors',
  '/dept/scheduling/dashboard': 'nav.crDashboard',
  '/dept/supplements/dashboard': 'nav.supDashboard',
  '/dept/supplements/estimating': 'nav.supEstimating',
  '/dept/supplements/price-lists': 'nav.supPriceLists',
  '/dept/supplements/verified-measurements': 'nav.supVerifiedMeasurements',
  '/dept/scheduling/scheduled': 'nav.crScheduled',
  '/dept/scheduling/calendar': 'nav.crCalendar',
  '/dept/scheduling/time-off': 'nav.timeOff',
  '/dept/scheduling/uploads': 'nav.crUploads',
  '/dept/scheduling/reports': 'nav.crReports',
  '/dept/scheduling/inspector-history': 'nav.inspectorHistory',
  '/dept/scheduling/activity': 'nav.crActivity',
  '/intake-review': 'nav.intakeReview',
  '/intake-finder': 'nav.intakeFinder',
  '/reports': 'nav.reports',
  '/requests': 'nav.requests',
  '/email-health': 'nav.emailHealth',
  '/complaints': 'nav.complaints',
  '/kudos': 'nav.kudos',
  '/evaluation': 'nav.evaluation',
  '/settings': 'nav.settings',
};

const QUEUE_TITLE: Record<string, string> = {
  acv: 'nav.acv',
  depreciation: 'nav.depreciation',
  'approved-repair': 'nav.approvedRepair',
  supplement: 'nav.supplementCheck',
  'invoice-todo': 'nav.invoiceTodo',
  review: 'nav.review',
  'waiting-clear': 'nav.waitingClear',
  mortgage: 'nav.mortgage',
};

function titleKeyFor(pathname: string): string {
  if (pathname.startsWith('/queue/')) {
    return QUEUE_TITLE[pathname.slice('/queue/'.length)] ?? 'nav.claims';
  }
  return TITLE_KEYS[pathname] ?? '';
}

export function AppShell() {
  const { me, loading, logout } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  /* EMAILS CON DESHACER: cambiar de pantalla manda lo que estaba esperando ("si cambias de pantalla y
     te vas a otro lado, ahí sí ya lo envía"); cerrar la pestaña también, con keepalive. */
  useEffect(() => { vaciarCola('navegacion'); }, [loc.pathname]);
  useEffect(() => {
    const alCerrar = () => { if (hayEnviosEsperando()) vaciarCola('cierre'); };
    window.addEventListener('pagehide', alCerrar);
    return () => window.removeEventListener('pagehide', alCerrar);
  }, []);
  // TÍTULO DE LA PESTAÑA (auditoría 2026-09-15): decía "Proactive Payments" en todas; con decenas
  // de pestañas abiertas no se distinguía cuál era cuál. La página del claim lo pisa con el nombre.
  useEffect(() => {
    const k = titleKeyFor(loc.pathname);
    const m = /^\/dept\/([^/]+)/.exec(loc.pathname);
    const nombre = k ? t(k) : (m ? m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1) : '');
    if (!/^\/claims\/\d+/.test(loc.pathname)) document.title = nombre ? `${nombre} · Proactive` : 'Proactive';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  /* LOS CONTADORES DE ARRIBA SE ENTERAN SOLOS.
     Se recontaban al cambiar de ruta, lo que alcanza para quien navega — pero alguien parado en
     una pantalla toda la mañana no veía entrar nada. `tick` agrega los otros dos disparadores:
     cada 90s y al volver a la pestaña. Con la pestaña oculta NO se consulta: veinte pestañas de
     fondo golpeando diez endpoints es carga pura sin nadie mirando. */
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => { if (!document.hidden) setTick((n) => n + 1); };
    const iv = window.setInterval(bump, 90_000);
    document.addEventListener('visibilitychange', bump);
    window.addEventListener('focus', bump);
    return () => {
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', bump);
      window.removeEventListener('focus', bump);
    };
  }, []);

  const isManager = me?.kind === 'tenant' && hasRole(me, 'manager');
  // UN SOLO GET PARA TODOS LOS CONTADORES (auditoría 2026-09-15): eran 9-13 requests en paralelo
  // más el de correo, cada 90 s y en CADA cambio de ruta (~35 requests al entrar, 12-16 por clic).
  // El cambio de ruta sigue refrescando (tras enviar pagos / subir autorizaciones el badge tiene
  // que bajar), pero con un mínimo de 15 s entre refrescos: navegar rápido por el menú no dispara
  // una ráfaga por pantalla.
  const [mailCounts, setMailCounts] = useState<Record<string, number>>({});
  const ultimoConteo = useRef(0);
  const [navTick, setNavTick] = useState(0);
  useEffect(() => {
    if (Date.now() - ultimoConteo.current > 15_000) setNavTick((n) => n + 1);
  }, [loc.pathname]);
  useEffect(() => {
    let alive = true;
    ultimoConteo.current = Date.now();
    api.get<{ counts: Record<string, number>; mail: Record<string, number> }>('/api/nav/badges')
      .then((r) => {
        if (!alive) return;
        setCounts(r.counts || {});
        setMailCounts(r.mail || {});
      })
      .catch(() => { /* el menú sigue; el próximo tick vuelve a intentar */ });
    // Refresca el badge de correo en vivo cuando se marca un email como leído (evento del buzón).
    const onMail = () => {
      api.get<{ counts: Record<string, number> }>('/api/mail/unread-counts').then((r) => { if (alive) setMailCounts(r.counts || {}); }).catch(() => {});
    };
    window.addEventListener('mail:changed', onMail);
    return () => { alive = false; window.removeEventListener('mail:changed', onMail); };
  }, [isManager, tick, navTick]);

  // El Contractor Inbox refresca su badge en vivo (al responder/resolver/asignar).
  useEffect(() => {
    let alive = true;
    const onChanged = () => {
      api.get<{ count: number }>('/api/contractor-inbox/unread-count')
        .then((r) => { if (alive) setCounts((c) => ({ ...c, contractor: r.count })); })
        .catch(() => {});
    };
    window.addEventListener('contractor:changed', onChanged);
    return () => { alive = false; window.removeEventListener('contractor:changed', onChanged); };
  }, []);

  // Casos de clientes: la página avisa cuando cambia un estado y el badge (tickets nuevos) se refresca.
  useEffect(() => {
    let alive = true;
    const onChanged = () => {
      api.get<{ concerns: unknown[] }>('/api/concerns?status=new')
        .then((r) => { if (alive) setCounts((c) => ({ ...c, concerns: r.concerns.length })); })
        .catch(() => {});
    };
    window.addEventListener('concerns:changed', onChanged);
    return () => { alive = false; window.removeEventListener('concerns:changed', onChanged); };
  }, []);

  // Badge del ítem "Screen recordings": los videos recibidos SIN ver. Fuente única = el poll del
  // banner "nuevo video" (NewVideoBanner), que publica el conteo por este evento (no hay segundo poll).
  useEffect(() => {
    const onInbox = (e: Event) => {
      const n = (e as CustomEvent<number>).detail;
      if (typeof n === 'number') setCounts((c) => ({ ...c, recordings: n }));
    };
    window.addEventListener('recordings:inbox', onInbox as EventListener);
    return () => window.removeEventListener('recordings:inbox', onInbox as EventListener);
  }, []);

  // Mail: # por atender por buzón (badges rojos) — viene en /api/nav/badges (ver arriba).

  // Buzones EXTRA (por PA, etc.) — todos los usuarios los ven.
  const [extraMailboxes, setExtraMailboxes] = useState<NavDef[]>([]);
  useEffect(() => {
    api.get<{ mailboxes: Array<{ key: string; label: string; email: string }> }>('/api/mailboxes')
      .then((r) => setExtraMailboxes((r.mailboxes || []).map((m) => ({ to: `/dept/${m.key}/inbox`, key: m.label,  mailKey: m.key }))))
      .catch(() => {});
  }, []);

  // Framework multi-departamento (aditivo). Si la API falla, solo queda Payments
  // y la app sigue funcionando igual que antes.
  const [depts, setDepts] = useState<{ key: string; name: string }[]>([]);
  useEffect(() => {
    let alive = true;
    api
      .get<{ departments: { key: string; name: string }[] }>('/api/departments')
      .then((r) => { if (alive) setDepts(r.departments); })
      .catch(() => { if (alive) setDepts([]); });
    return () => { alive = false; };
  }, []);

  // Depto activo PERSISTE (no se cae a Payments al usar items comunes /finder, /claims…).
  const navigate = useNavigate();
  const [activeDept, setActiveDept] = useState<string>(
    () => deptForPath(loc.pathname) || storedDept() || 'payments',
  );
  useEffect(() => {
    const d = deptForPath(loc.pathname);
    if (d && d !== activeDept) { setActiveDept(d); localStorage.setItem('activeDept', d); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);
  const selectDept = (key: string) => {
    setActiveDept(key);
    localStorage.setItem('activeDept', key);
    // Supplements abre por defecto en su Dashboard (gráficas); el board queda un click al lado.
    navigate(key === 'payments' ? '/' : key === 'supplements' ? '/dept/supplements/dashboard' : `/dept/${key}`);
  };
  // Primer ingreso (sin depto elegido aún): aterriza en el board de SU departamento — antes
  // TODOS caían al board de Payments (reclamo general). Solo aplica si nunca eligió uno.
  const myDefaultDept = useMyDefaultDepartment();
  useEffect(() => {
    if (!localStorage.getItem('activeDept') && myDefaultDept?.key && myDefaultDept.key !== activeDept) {
      selectDept(myDefaultDept.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myDefaultDept?.key]);

  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem('navCollapsed') === '1');
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      localStorage.setItem('navCollapsed', v ? '0' : '1');
      return !v;
    });
  };

  // Menú móvil tipo Monday: el menú es una pantalla completa; al tocar un ítem se
  // abre el contenido full y se cierra el menú. Se cierra solo al cambiar de ruta.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  // Estatus (colas internas) de TODOS los departamentos, para los accordions del menú.
  // Se cargan en paralelo (son requests chicos) → los accordions y el buscador tienen data.
  const [deptStatusMap, setDeptStatusMap] = useState<Record<string, { key: string; label: string }[]>>({});
  // Cuántos claims hay en cada cola del depto (badge del menú, ej. "COC 12").
  const [deptCounts, setDeptCounts] = useState<Record<string, Record<string, number>>>({});
  useEffect(() => {
    let alive = true;
    const keys = depts.map((d) => d.key).filter((k) => k !== 'payments');
    if (!keys.length) return;
    Promise.all(
      keys.map((k) =>
        api.get<{ statuses: { key: string; label: string }[] }>(`/api/departments/${k}/statuses`)
          .then((r) => [k, r.statuses.filter((s) => s.key !== 'Submit Supplement')] as const)
          .catch(() => [k, []] as const),
      ),
    ).then((pairs) => { if (alive) setDeptStatusMap(Object.fromEntries(pairs)); });
    Promise.all(
      keys.map((k) =>
        api.get<{ counts: Record<string, number> }>(`/api/departments/${k}/status-counts`)
          .then((r) => [k, r.counts ?? {}] as const)
          .catch(() => [k, {} as Record<string, number>] as const),
      ),
    ).then((pairs) => { if (alive) setDeptCounts(Object.fromEntries(pairs)); });
    return () => { alive = false; };
  }, [depts]);

  const activeDeptName = depts.find((d) => d.key === activeDept)?.name ?? '';

  // Buscador del menú izquierdo (reemplaza el dropdown de departamentos): filtra los ítems.
  const [menuQ, setMenuQ] = useState('');
  // Buscador Finder de la barra superior: es EL buscador global (pedido de Manuel — se quitó el de
  // Claims). Busca EN VIVO mientras se escribe (debounce) y desde CUALQUIER página → /finder.
  const [finderQ, setFinderQ] = useState('');
  const submitFinder = () => { navigate(`/finder${finderQ.trim() ? `?q=${encodeURIComponent(finderQ.trim())}` : ''}`); };
  // Búsqueda en vivo: a los 300ms de escribir (≥2 chars) navega al Finder con la query. Si ya
  // estamos en /finder, reemplaza (no ensucia el historial); si no, entra al Finder desde donde sea.
  useEffect(() => {
    const term = finderQ.trim();
    if (term.length < 2) return;
    const h = setTimeout(() => {
      navigate(`/finder?q=${encodeURIComponent(term)}`, { replace: loc.pathname === '/finder' });
    }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finderQ]);
  // Accordions de departamentos: recogidos por defecto; el depto de la ruta actual arranca abierto.
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(() => new Set(activeDept ? [activeDept] : []));
  const toggleDept = (key: string) => setExpandedDepts((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  // Al navegar a un depto, abre su accordion (sin cerrar los demás que el usuario dejó abiertos).
  useEffect(() => {
    const d = deptForPath(loc.pathname);
    if (d) setExpandedDepts((s) => (s.has(d) ? s : new Set(s).add(d)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  // Filtro del buscador del menú (case-insensitive; vacío = todo pasa).
  const q = menuQ.trim().toLowerCase();
  const matchLabel = (label: string) => !q || label.toLowerCase().includes(q);

  // Fila de nav genérica (link + badge). Respeta el filtro del buscador.
  const navRow = (it: NavDef, label: string, opts?: { indent?: boolean; n?: number }) => {
    const Icon = iconFor(it.to);
    return (
      <NavLink
        /* key por ruta+label: Supplements repite la ruta de su dashboard (fila Dashboard +
           herramienta "Supp dashboard") y con la ruta sola React avisaba key duplicada. */
        key={`${it.to}|${label}`}
        to={it.to}
        end={it.to === '/'}
        title={label}
        className={({ isActive }) => `nav-item${isActive ? ' active' : ''}${opts?.indent ? ' nav-sub' : ''}`}
      >
        <span className="ico"><Icon size={17} strokeWidth={1.9} /></span>
        <span className="nav-label">{label}</span>
        {it.badge && counts[it.badge] > 0 && <span className="nav-badge">{counts[it.badge]}</span>}
        {/* cuántos claims hay en esa cola del depto (ej. "COC 12") */}
        {opts?.n != null && opts.n > 0 && <span className="nav-badge">{opts.n}</span>}
        {it.mailKey && mailCounts[it.mailKey] > 0 && <span className="nav-badge">{mailCounts[it.mailKey]}</span>}
      </NavLink>
    );
  };
  // El `min` es el piso por ROL; el `perm` es un permiso que se asigna por persona. Un ítem con
  // los dos entra si cumple CUALQUIERA de los dos (`min` se salta cuando hay `perm`): Settings lo
  // ve el manager por rol y también quien tenga el permiso de plantillas sin serlo.
  const tienePerm = (p: NavDef['perm']) =>
    p === 'templates' ? canMakeTemplates(me)
      : p === 'templatesHub' ? canOpenTemplates(me)
      : p === 'agentsHub' ? canOpenAgents(me)
      : p === 'superBoard' ? canSeeSuperBoard(me)
      : p === 'canPay' ? puedeHacerPagos(me)
      : p === 'printChecks' ? puedeImprimirCheques(me)
      : canSeeCallMonitoring(me);
  const puedeVer = (it: NavDef) => {
    // El flag de infra manda sobre todo lo demás: si el backend contesta 404, no hay rol ni permiso
    // que haga útil el link.
    if (it.flag === 'portal' && !(me?.kind === 'tenant' && me.user?.portal_enabled)) return false;
    const porRol = !it.min || (me?.kind === 'tenant' && hasRole(me, it.min));
    if (!it.perm) return porRol;
    // Con `perm`, el rol solo suma si el ítem además declara un `min` (Settings). Un ítem que es
    // SOLO permiso (Call Monitoring) no se abre por rol: esa es justamente su razón de ser.
    return tienePerm(it.perm) || (!!it.min && porRol);
  };
  const renderNav = (items: NavDef[], opts?: { indent?: boolean }) =>
    items
      .filter(puedeVer)
      .filter((it) => matchLabel(it.key.startsWith('nav.') ? t(it.key) : it.key)) // extras usan label crudo
      .map((it) => navRow(it, it.key.startsWith('nav.') ? t(it.key) : it.key, opts));

  // Ítems INTERNOS de un departamento (Dashboard + colas/estatus + herramientas), para el accordion.
  // Cada depto con dashboard propio (2026-08-09). Payments va directo a /payments para que
  // el landing "/" (que redirige por depto) no rebote a un usuario de otro departamento.
  const deptDashTo = (key: string) =>
    key === 'payments' ? '/payments'
    : key === 'supplements' ? '/dept/supplements/dashboard'
    : key === 'intake' ? '/dept/intake/dashboard'
    : key === 'claims' ? '/dept/claims/dashboard'
    : `/dept/${key}`;
  const renderDeptItems = (key: string) => {
    const rows: React.ReactNode[] = [];
    // Dashboard del depto.
    if (matchLabel(t('nav.dashboard'))) rows.push(navRow({ to: deptDashTo(key), key: 'nav.dashboard' }, t('nav.dashboard'), { indent: true }));
    /* REVIEW ESTIMATE SE LISTA UNA SOLA VEZ (pedido del dueño 2026-08-27: "Review Estimate será
       uno solo, ya no será duplicado").
       Acá vivía una fila fija de Review Estimate para el menú de Supplements, porque entre las
       migs 0374 y 0416 esa columna pertenecía al departamento `estimators` y solo aparecía en el
       otro menú. Con los dos departamentos unidos, `deptStatusMap['supplements']` ya la trae —
       dejar la fila fija la mostraría DOS VECES, que es justo lo que se está arreglando. */
    if (key === 'payments') {
      rows.push(...renderNav(PAYMENTS_NAV, { indent: true }));
    } else {
      for (const s of deptStatusMap[key] ?? []) {
        if (!matchLabel(s.label)) continue;
        rows.push(navRow({ to: `/dept/${key}/queue/${encodeURIComponent(s.key)}`, key: s.label }, s.label, { indent: true, n: deptCounts[key]?.[s.key] }));
      }
      // Sin duplicar la fila Dashboard: en Supplements la herramienta 'nav.supDashboard' apunta a
      // la MISMA ruta que la fila Dashboard de arriba y el menú mostraba "Dashboard" dos veces.
      rows.push(...renderNav((DEPT_TOOLS[key] ?? []).filter((it) => it.to !== deptDashTo(key)), { indent: true }));
    }
    return rows;
  };

  // Accordion de un departamento. Al buscar se auto-expande; oculta el depto si nada matchea.
  const renderDeptAccordion = (d: { key: string; name: string }) => {
    const items = renderDeptItems(d.key);
    const nameMatches = matchLabel(d.name);
    if (q && !nameMatches && items.length === 0) return null; // sin coincidencias en este depto
    const open = expandedDepts.has(d.key) || !!q; // buscando → todo abierto
    return (
      <div key={d.key} className="nav-dept">
        <div className={`nav-item nav-dept-head${activeDept === d.key ? ' active-dept' : ''}`} style={{ padding: 0 }}>
          {/* La flecha SOLO togglea; el nombre navega al DASHBOARD del depto (y expande). */}
          <button type="button" className="nav-dept-caret" onClick={() => toggleDept(d.key)} aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? <ChevronDown size={16} strokeWidth={2} /> : <ChevronRight size={16} strokeWidth={2} />}
          </button>
          <NavLink to={deptDashTo(d.key)} end className="nav-dept-name"
            onClick={() => {
              setExpandedDepts((s) => (s.has(d.key) ? s : new Set(s).add(d.key)));
              // Elegir el departamento en el menú tiene que PERSISTIRLO. Payments es el único
              // cuya ruta es `/` y no `/dept/payments`, así que el efecto de deptForPath no lo
              // detectaba: clickear "Payments" nunca lo dejaba como departamento de trabajo.
              setActiveDept(d.key);
              localStorage.setItem('activeDept', d.key);
            }}>
            {d.name}
          </NavLink>
        </div>
        {open && <div className="nav-dept-body">{items}</div>}
      </div>
    );
  };

  if (loading) return <div className="page-empty">{t('common.loading')}</div>;
  if (!me) return <Navigate to="/login" replace />;
  if (me.kind === 'platform') return <Navigate to="/platform" replace />;
  // Los inspectores SOLO ven su app de campo — nunca el shell de oficina.
  if (me.kind === 'tenant' && me.user.role === 'inspector') return <Navigate to="/inspector" replace />;
  // Cambio forzado de contraseña en el primer login (bloquea la app).
  if (me.kind === 'tenant' && me.user.must_change_password) return <ForcePasswordChange />;

  return (
    <PresenceProvider>
    {/* El ShareProvider (socket, tarjeta z 4100, visor z 450, píldora) vive en App.tsx envolviendo
        las rutas: sirve a este shell Y a la app del inspector (/inspector), que recibe el request
        sin pasar por acá (spec §4.11). Acá solo queda el botón de la barra. */}
    {/* Barras fijas arriba de CUALQUIER sección: la roja de alertas de inspector (no-show/
        urgente) y la azul del Employee of the Month. Apiladas, no superpuestas. */}
    <div className="top-banners">
      {/* Primero de todo: si esta pestaña quedó con el código viejo, ningún otro aviso de acá
          abajo es confiable — corren con el JavaScript del día que la abrieron. */}
      <AppUpdateBar />
      <InspectorAlertBanner />
      {/* COC que lleva 3+ días sin enviarse: es plata sin cobrar. */}
      {/* Un SMS del homeowner sin contestar es alguien esperando: la alerta va ARRIBA de todo
          y escala sola hasta que se responda. */}
      <SmsAlert />
      {/* La ventana que exige el .ESX al subir un estimado nuestro (bloqueante, sin cerrar). */}
      <EsxRequiredHost />
      {/* Los emails con 10 s para deshacer (abajo a la izquierda). */}
      <UndoSendHost />
      <CocAlertBanner />
      {/* Redes (dueño 2026-11-02): un mensaje o comentario de Facebook / Instagram que necesita una persona, o un envío fallido. */}
      <SocialAlertBanner />
      {/* "Nuevo video": alguien te mandó una grabación que no viste — click y se ve en el centro. */}
      <NewVideoBanner />
          <FalloDeCargaBar />
      {/* El avión con el cartel: anuncios internos. */}
      <BroadcastBanner />
      {/* Tu recibo de sueldo está listo. */}
      <PayslipBanner />
      <EomAnnouncementModal />
    </div>
    {/* Flecha "subir al tope", abajo a la derecha en TODA la app: las pantallas del claim son
        largas y el trabajo va del final (leer el contrato) al principio (decidir). */}
    <ScrollTopButton />
    <div className={`shell${collapsed ? ' collapsed' : ''}${menuOpen ? ' menu-open' : ''}`}>
      <aside className="sidebar" onClick={(e) => { if ((e.target as HTMLElement).closest('a')) setMenuOpen(false); }}>
        <div className="brand">
          <img src={logoWhite} alt={me.user.company_name || 'Proactive'} className="brand-logo" />
          <button className="nav-collapse" onClick={toggleCollapsed} title={collapsed ? 'Expand' : 'Collapse'} aria-label="Toggle menu">
            {collapsed ? <PanelLeftOpen size={18} strokeWidth={1.9} /> : <PanelLeftClose size={18} strokeWidth={1.9} />}
          </button>
          {/* Cerrar el menú full-screen (solo mobile). */}
          <button className="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <ArrowLeft size={20} strokeWidth={2} />
          </button>
        </div>
        {/* KPI del día, abajo del logo (dueño 2026-10-28): cada uno ve lo suyo de hoy. Al super-manager
            le abre la pestaña KPI de Company, donde están todos. */}
        {hasRole(me, 'office') && <KpiDelDia linkTo={hasRole(me, 'super-manager') ? '/company?tab=kpi' : undefined} />}
        {/* Buscador del menú (reemplaza el dropdown de departamentos): filtra todo el menú. */}
        <div className="menu-search">
          <Search size={15} strokeWidth={2} className="menu-search-ico" />
          <input
            className="menu-search-input"
            placeholder={t('nav.searchMenu')}
            value={menuQ}
            onChange={(e) => setMenuQ(e.target.value)}
          />
          {menuQ && <button className="menu-search-clear" onClick={() => setMenuQ('')} aria-label="Clear"><X size={14} strokeWidth={2} /></button>}
          {/* Atajo del buscador global (CommandSearch): visible como en Linear; click lo abre. */}
          {!menuQ && (
            <kbd className="menu-kbd" title="⌘K" onClick={() => window.dispatchEvent(new Event('cmdk:open'))}>⌘K</kbd>
          )}
        </div>
        <nav className="nav">
          {/* 1) INBOXES primero: To Do + Contractor Inbox + SMS + buzones.
              En el TELÉFONO (≤900px) la mensajería está repetida con el tab Inbox (el hub), así
              que ahí solo quedan To Do, Company y JobFlow (pedido del dueño 2026-08-09); el resto
              va dentro de .nav-hub-dup, que el CSS esconde en móvil. Desktop y 901–1024 igual. */}
          <div>
            <div className="nav-section">{t('nav.inboxes')}</div>
            {renderNav(INBOX_TOP_NAV.filter((it) => !HUB_DUP.has(it.to)))}
            <div className="nav-hub-dup">
              {renderNav(INBOX_TOP_NAV.filter((it) => HUB_DUP.has(it.to)))}
              {renderNav(MAIL_NAV)}
              {extraMailboxes.length > 0 && renderNav(extraMailboxes)}
            </div>
            {/* Lo que en desktop vive en la barra de arriba y en el teléfono no tiene puerta. */}
            <div className="nav-mobile-only">
              {renderNav(MOBILE_ONLY_NAV)}
            </div>
          </div>

          {/* 2) DEPARTAMENTOS: accordions recogidos por defecto; despliega sus colas/estatus. */}
          <div>
            <div className="nav-section">{t('nav.departments')}</div>
            {depts.map((d) => renderDeptAccordion(d))}
          </div>

          {/* 3) MANAGEMENT al final. */}
          <div>
            <div className="nav-section">{t('nav.management')}</div>
            {renderNav(MANAGEMENT_NAV)}
          </div>
        </nav>
        <div className="sidebar-foot">
          <div className="sidebar-user">
            {/* Tu nombre + avatar → tu perfil (info, desempeño, resumen de quejas del año, subir foto). */}
            <NavLink to={`/employee/${me.user.id}`} title={t('prof.myProfile')} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'inherit', textDecoration: 'none' }}>
              <Avatar userId={me.user.id} name={me.user.name} size={30} />
              <span style={{ display: 'grid', lineHeight: 1.25, minWidth: 0 }}>
                <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{me.user.name}</b>
                <span style={{ fontSize: 'var(--fs-2xs)', color: '#aab4c6' }}>{me.user.role}</span>
              </span>
            </NavLink>
            <a onClick={() => logout()} style={{ cursor: 'pointer', color: '#aab4c6', fontSize: 'var(--fs-2xs)', marginTop: 4, display: 'inline-block' }}>
              {t('nav.logout')}
            </a>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          {/* Abrir el menú full-screen (solo mobile 901–1024px; en ≤900 lo reemplaza la tab bar). */}
          <button className="mobile-menu-open" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Menu size={22} strokeWidth={2} />
          </button>
          {/* APPBAR MÓVIL limpio (mockup B-mobile, aprobado "logo en morado"): cuadrito del logo
              + título de la pantalla + campana + avatar. Solo existe en ≤900px (CSS). */}
          <span className="m-applogo" aria-hidden="true"><img src="/favicon.png" alt="" /></span>
          <h1 className="topbar-title">{(() => {
            const k = titleKeyFor(loc.pathname);
            if (k) return t(k);
            // En rutas /dept/<key>/... el título es el depto DE LA RUTA, no el último clickeado
            // en el menú (activeDept): estando en Claims Mail salía "Supplements" (2026-08-09).
            const m = /^\/dept\/([^/]+)/.exec(loc.pathname);
            const routeDept = m ? depts.find((d) => d.key === m[1])?.name : undefined;
            return routeDept || activeDeptName || '';
          })()}</h1>
          {/* Finder como BUSCADOR global (pedido) + accesos rápidos en la barra superior. */}
          <div className="topbar-finder">
            <Search size={15} strokeWidth={2} className="topbar-finder-ico" />
            <input
              className="topbar-finder-input"
              placeholder={t('nav.finder')}
              value={finderQ}
              onChange={(e) => setFinderQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitFinder(); }}
            />
          </div>
          <nav className="topbar-nav">
            {TOPBAR_NAV
              .filter(puedeVer)
              .map((it) => {
                const Icon = iconFor(it.to);
                return (
                  <NavLink key={it.to} to={it.to} title={t(it.key)} className={({ isActive }) => `topbar-link${isActive ? ' active' : ''}`}>
                    <Icon size={17} strokeWidth={1.9} />
                    <span className="topbar-link-label">{t(it.key)}</span>
                    {it.badge && counts[it.badge] > 0 && <span className="topbar-badge">{counts[it.badge]}</span>}
                  </NavLink>
                );
              })}
          </nav>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
            {/* Campana SOLO móvil (el appbar limpio del mockup): notificaciones con badge. */}
            <NavLink to="/notifications" className="m-appbell" aria-label={t('nav.notifications')}>
              <Bell size={19} strokeWidth={1.75} />
              {counts.notifications > 0 && <span className="mtab-badge" style={{ position: 'static' }}>{counts.notifications}</span>}
            </NavLink>
            {/* Activar push del navegador (solo si el permiso aún no fue concedido). */}
            <span className="topbar-push"><EnablePushButton /></span>
            {/* Reloj de fichaje. Se dibuja solo si está habilitado para este usuario.
                En móvil se OCULTA (pedido del dueño 2026-08-08: en el teléfono estorba). */}
            <span className="topbar-clock"><ClockButton /></span>
            {/* Compartir pantalla con un compañero (office+, escritorio). */}
            <ShareButton />
            {/* La escalera: alguien de oficina que también es inspector salta a su app de campo. */}
            {puedeModoInspector(me) && <InspectorModeButton />}
            {/* Idioma, tema, disponibilidad y perfil viven ACÁ ADENTRO. Estaban sueltos en la barra
                y se comían el ancho: con la pantalla dividida los badges de Messaging y
                Notifications quedaban fuera de la vista. Son ajustes de una vez por semana; los
                contadores se miran todo el día. */}
            <ProfileMenu userId={me.user.id} name={me.user.name} />
          </div>
        </header>
        {/* ⌘K vive en el shell: se abre desde cualquier pantalla, sin ocupar lugar en la barra. */}
        <CommandSearch />
        {/* Tab bar inferior (Fase 5, solo ≤900px por CSS): Claims · Inbox · Panel · Alertas · Menú.
            Working views y monitoreo de empleados quedan solo en desktop (decisión del dueño);
            siguen accesibles desde el Menú, sin optimizar para el teléfono. */}
        <nav className="mtabbar">
          <NavLink to="/claims" className={({ isActive }) => `mtab${isActive ? ' on' : ''}`}>
            <FolderKanban size={20} strokeWidth={1.75} />
            <span>{t('nav.claims')}</span>
          </NavLink>
          <NavLink to="/inbox" className={({ isActive }) => `mtab${isActive ? ' on' : ''}`}>
            <Inbox size={20} strokeWidth={1.75} />
            <span>{t('mnav.inbox')}</span>
            {(counts.messaging || 0) + (counts.sms || 0) + (mailCounts.mine || 0) > 0 && (
              <span className="mtab-badge">{(counts.messaging || 0) + (counts.sms || 0) + (mailCounts.mine || 0)}</span>
            )}
          </NavLink>
          <NavLink to="/" end className={({ isActive }) => `mtab${isActive ? ' on' : ''}`}>
            <LayoutDashboard size={20} strokeWidth={1.75} />
            <span>{t('mnav.panel')}</span>
          </NavLink>
          <NavLink to="/notifications" className={({ isActive }) => `mtab${isActive ? ' on' : ''}`}>
            <Bell size={20} strokeWidth={1.75} />
            <span>{t('mnav.alerts')}</span>
            {counts.notifications > 0 && <span className="mtab-badge">{counts.notifications}</span>}
          </NavLink>
          <button type="button" className="mtab" onClick={() => setMenuOpen(true)}>
            <Menu size={20} strokeWidth={1.75} />
            <span>{t('mnav.menu')}</span>
          </button>
        </nav>
        <div className={`content${(loc.pathname.startsWith('/dept/scheduling') || loc.pathname.startsWith('/claims') || MANAGEMENT_NAV.some((n) => loc.pathname === n.to || loc.pathname.startsWith(n.to + '/'))) ? ' content-wide' : ''}`}>
          <Outlet />
        </div>
      </div>
    </div>
    </PresenceProvider>
  );
}
